import { defineStore } from "pinia";
import { ref } from "vue";
import { API } from "@/queries/index";
import { AxiosError } from "axios";
import { APIResponse, APIResponseError } from "@/types/api";
import { handleResponse, handleResponseError } from "@/utils/api";
import { RequisitionPayload, RequisitionResponse, RequisitionUpdateStatus } from "@/types/interface/requisition";

export const useRequisitionStore = defineStore("requisitionStore", () => {
  const requisition = ref<RequisitionResponse>();
  const requisitions = ref<RequisitionResponse[]>([]);

  function initRequisition(data: any) {
    requisitions.value = data?.data;
  }

  function addNewRequisition(data: any) {
    requisition.value = data;
  }

  async function dispatchGetList(page = 1, limit = 10, queryParam = "") : Promise<APIResponse<null>> {
    try {
      const { status, data } = await API.requisition.get(page, limit, queryParam);
      initRequisition(data);
      return handleResponse(status, data);
    } catch (error) {
      const _error = error as AxiosError<APIResponseError>;
      const errorResponseData = _error.response?.data
      return handleResponseError(errorResponseData)
    }
  }

  async function dispatchGetListSubmission(page = 1, limit = 10, queryParam = "") : Promise<APIResponse<null>> {
    try {
      const { status, data } = await API.requisition.getSubsmission(page, limit, queryParam);
      initRequisition(data);
      return handleResponse(status, data);
    } catch (error) {
      const _error = error as AxiosError<APIResponseError>;
      const errorResponseData = _error.response?.data
      return handleResponseError(errorResponseData)
    }
  }

  async function dispatchGetListApproval(page = 1, limit = 10, queryParam = "") : Promise<APIResponse<null>> {
    try {
      const { status, data } = await API.requisition.getApproval(page, limit, queryParam);
      initRequisition(data);
      return handleResponse(status, data);
    } catch (error) {
      const _error = error as AxiosError<APIResponseError>;
      const errorResponseData = _error.response?.data
      return handleResponseError(errorResponseData)
    }
  }

  async function dispatchGetListIncoming(page = 1, limit = 10, queryParam = "") : Promise<APIResponse<null>> {
    try {
      const { status, data } = await API.requisition.getIncoming(page, limit, queryParam);
      initRequisition(data);
      return handleResponse(status, data);
    } catch (error) {
      const _error = error as AxiosError<APIResponseError>;
      const errorResponseData = _error.response?.data
      return handleResponseError(errorResponseData)
    }
  }

  async function dispatchDetail(idRequisition = "") : Promise<APIResponse<null>> {
    try {
      const { status, data } = await API.requisition.detail(idRequisition);
      addNewRequisition(data);
      return handleResponse(status, data);
    } catch (error) {
      const _error = error as AxiosError<APIResponseError>;
      const errorResponseData = _error.response?.data
      return handleResponseError(errorResponseData)
    }
  }

  async function dispatchCreate(payload: RequisitionPayload) : Promise<APIResponse<null>> {
    try {
      const { status, data } = await API.requisition.create(payload);
      addNewRequisition(data);
      return handleResponse(status, data);
    } catch (error) {
      const _error = error as AxiosError<APIResponseError>;
      const errorResponseData = _error.response?.data
      return handleResponseError(errorResponseData)
    }
  }

  async function dispatchUpdate(idRequisition = "", payload: RequisitionPayload) : Promise<APIResponse<null>> {
    try {
      const { status, data } = await API.requisition.update(idRequisition, payload);
      addNewRequisition(data);
      return handleResponse(status, data);
    } catch (error) {
      const _error = error as AxiosError<APIResponseError>;
      const errorResponseData = _error.response?.data
      return handleResponseError(errorResponseData)
    }
  }

  async function dispatchUpdateStatus(idRequisition = "", payload: RequisitionUpdateStatus) : Promise<APIResponse<null>> {
    try {
      const { status, data } = await API.requisition.updateStatusRequisition(idRequisition, payload);
      return handleResponse(status, data);
    } catch (error) {
      const _error = error as AxiosError<APIResponseError>;
      const errorResponseData = _error.response?.data
      return handleResponseError(errorResponseData)
    }
  }

  async function dispatchDelete(idRequisition = "") : Promise<APIResponse<null>> {
    try {
      const { status, data } = await API.requisition.remove(idRequisition);
      return handleResponse(status, data);
    } catch (error) {
      const _error = error as AxiosError<APIResponseError>;
      const errorResponseData = _error.response?.data
      return handleResponseError(errorResponseData)
    }
  }

  async function dispatchSubmitDraft(idRequisition = "") : Promise<APIResponse<null>> {
    try {
      const { status, data } = await API.requisition.submitDraft(idRequisition);
      return handleResponse(status, data);
    } catch (error) {
      const _error = error as AxiosError<APIResponseError>;
      const errorResponseData = _error.response?.data
      return handleResponseError(errorResponseData)
    }
  }

  return {
    requisition,
    requisitions,
    initRequisition,
    dispatchCreate,
    dispatchUpdate,
    dispatchUpdateStatus,
    dispatchDetail,
    dispatchGetList,
    dispatchGetListSubmission,
    dispatchGetListApproval,
    dispatchGetListIncoming,
    dispatchDelete,
    dispatchSubmitDraft,
  };
});